import { ElementType } from 'react'
import { AddMoreIcon, BackdropIcon, GifIcon, StickerIcon, SuperCutIcon, VideoIcon } from '../components/icons'
import { ALL_ANIMATED_MIME_TYPES, ANIMATED_MIME_TYPES, STILL_MIME_TYPES, VIDEO_MIME_TYPES } from '../constants'
import getLabelFromMimeTypes from '../utils/get-mime-types-label'
import CreationToolOutputType from './output-type'
import CreationToolUserRestriction from './user-restriction'

/**
 * File and url buttons on the welcome screen.
 */
enum CreationToolWelcomeButton {
    Gif = 'CT_WELCOME_BUTTON_GIF',
    Sticker = 'CT_WELCOME_BUTTON_STICKER',
    Upload = 'CT_WELCOME_BUTTON_UPLOAD',
    UploadSticker = 'CT_WELCOME_BUTTON_UPLOAD_STICKER',
    UploadVideo = 'CT_WELCOME_BUTTON_UPLOAD_VIDEO',
    Backdrop = 'CT_WELCOME_BUTTON_BACKDROP',
    SuperCut = 'CT_WELCOME_BUTTON_SUPER_CUT',
    UploadAddMore = 'CT_UPLOAD_ADD_MORE',
}

namespace CreationToolWelcomeButton {
    /**
     * Returns the button's title.
     */
    export function title(button: CreationToolWelcomeButton): string {
        switch (button) {
            case CreationToolWelcomeButton.Gif:
            case CreationToolWelcomeButton.Upload:
                return 'GIF'
            case CreationToolWelcomeButton.Sticker:
            case CreationToolWelcomeButton.UploadSticker:
                return 'Sticker'
            case CreationToolWelcomeButton.Backdrop:
                return 'Backdrop'
            case CreationToolWelcomeButton.UploadVideo:
                return 'Clip'
            case CreationToolWelcomeButton.SuperCut:
                return 'Super Cut'
            case CreationToolWelcomeButton.UploadAddMore:
                return 'Add More Files'
        }
    }

    /**
     * Returns the button's description.
     */
    export function description(button: CreationToolWelcomeButton): string {
        const mimeTypes = CreationToolWelcomeButton.mimeTypes(button)
        switch (button) {
            case CreationToolWelcomeButton.Gif:
            case CreationToolWelcomeButton.Sticker:
                return `Accepted formats: ${getLabelFromMimeTypes(mimeTypes)}.`
            case CreationToolWelcomeButton.Upload:
            case CreationToolWelcomeButton.UploadAddMore:
                return `Upload a ${getLabelFromMimeTypes(mimeTypes)}.`
            case CreationToolWelcomeButton.UploadSticker:
                return `Upload a ${getLabelFromMimeTypes(mimeTypes)} with transparency.`
            case CreationToolWelcomeButton.UploadVideo:
                return `Upload an ${getLabelFromMimeTypes(mimeTypes)} with audio.`
            case CreationToolWelcomeButton.Backdrop:
                return 'Spice up your video calls with our custom virtual background tool.'
            case CreationToolWelcomeButton.SuperCut:
                return 'Automagically create a set of GIFs & Videos from a longer clip.'
        }
    }

    /**
     * Returns the button's icon component.
     */
    export function icon(button: CreationToolWelcomeButton): ElementType {
        switch (button) {
            case CreationToolWelcomeButton.Gif:
            case CreationToolWelcomeButton.Upload:
                return GifIcon
            case CreationToolWelcomeButton.Sticker:
            case CreationToolWelcomeButton.UploadSticker:
                return StickerIcon
            case CreationToolWelcomeButton.UploadVideo:
                return VideoIcon
            case CreationToolWelcomeButton.Backdrop:
                return BackdropIcon
            case CreationToolWelcomeButton.SuperCut:
                return SuperCutIcon
            case CreationToolWelcomeButton.UploadAddMore:
                return AddMoreIcon
        }
    }

    /**
     * Returns the button's url if it has one.
     */
    export function url(button: CreationToolWelcomeButton): string {
        switch (button) {
            case CreationToolWelcomeButton.Backdrop:
                return '/backdrop'
            case CreationToolWelcomeButton.SuperCut:
                return '/supercut'
            default:
                return ''
        }
    }

    /**
     * Returns the button's accepted mimeTypes.
     */
    export function mimeTypes(button: CreationToolWelcomeButton): string[] {
        switch (button) {
            case CreationToolWelcomeButton.Gif:
                return [...STILL_MIME_TYPES, ...ALL_ANIMATED_MIME_TYPES]
            case CreationToolWelcomeButton.Sticker:
                return [...STILL_MIME_TYPES]
            case CreationToolWelcomeButton.Upload:
            case CreationToolWelcomeButton.UploadAddMore:
                return [...ALL_ANIMATED_MIME_TYPES]
            case CreationToolWelcomeButton.UploadSticker:
                return [...ANIMATED_MIME_TYPES]
            case CreationToolWelcomeButton.UploadVideo:
                return [...VIDEO_MIME_TYPES]
            default:
                return []
        }
    }

    /**
     * Returns the button's creation output type.
     */
    export function outputType(button: CreationToolWelcomeButton): CreationToolOutputType {
        switch (button) {
            case CreationToolWelcomeButton.Sticker:
            case CreationToolWelcomeButton.UploadSticker:
                return CreationToolOutputType.Sticker
            case CreationToolWelcomeButton.UploadVideo:
                return CreationToolOutputType.Video
            case CreationToolWelcomeButton.Backdrop:
                return CreationToolOutputType.Backdrop
            default:
                return CreationToolOutputType.Gif
        }
    }

    /**
     * Returns user restrictions for the output type, if it has one.
     */
    export function userRestriction(button: CreationToolWelcomeButton): CreationToolUserRestriction | null {
        switch (button) {
            case CreationToolWelcomeButton.SuperCut:
                return CreationToolUserRestriction.CanAccessSuperCut
            default:
                return CreationToolOutputType.userRestriction(CreationToolWelcomeButton.outputType(button))
        }
    }

    /**
     * Returns whether or not the button is new.
     */
    export function isNew(button: CreationToolWelcomeButton): boolean {
        switch (button) {
            case CreationToolWelcomeButton.Sticker:
                return true
            default:
                return false
        }
    }
}

export default CreationToolWelcomeButton
